import {AuthenticatedApiClient} from '@/matic/lib/api/authenticated-api-client';
import type {
  ManagedFile,
  FilesListResponse,
  FileCategory,
  UploadUrlResult,
  ContentUrlResult,
  CreateFileFromStagingInput,
  CreateFileFromRelayInput,
  UpdateFileInput,
  ListFilesParams,
  FileAssociation,
  MultipartInitResult,
  CompleteMultipartInput,
} from '../types/files-types';

/** PUT a body to a presigned S3 URL via XHR, reporting upload progress.
 *  Resolves with the response ETag header (needed for multipart part assembly;
 *  null for single-PUT where it isn't used). */
async function xhrPut(
  url: string,
  body: Blob,
  options: {contentType?: string; onProgress?: (loaded: number) => void} = {}
): Promise<{etag: string | null}> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    if (options.contentType) {
      xhr.setRequestHeader('Content-Type', options.contentType);
    }

    const {onProgress} = options;
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) onProgress(event.loaded);
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({etag: xhr.getResponseHeader('ETag')});
      } else {
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    });
    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed: network error'));
    });
    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });
    xhr.send(body);
  });
}

/* eslint-disable @typescript-eslint/naming-convention */
type RawFileAssociation = {
  id: string;
  object_type_id: string;
  object_record_id: string | null;
  object_type_name: string | null;
  object_record_name: string | null;
};

type RawManagedFile = {
  id: string;
  name: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  sha256: string | null;
  category_id: string | null;
  labels?: string[];
  source_kind: string | null;
  source_id: string | null;
  cui: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  email_sent_at: string | null;
  associations: RawFileAssociation[];
};

type RawFilesListResponse = {items: RawManagedFile[]; total: number};

type RawFileCategory = {
  id: string;
  name: string;
  sort_order: number;
  deleted_at: string | null;
};

type RawUploadUrlResult = {url: string; file_id: string; expires_in: number};
type RawContentUrlResult = {url: string; expires_in: number};
type RawMultipartInitResult = {
  file_id: string;
  upload_id: string;
  part_size: number;
  part_count: number;
};
type RawMultipartPartUrls = {urls: Record<string, string>};
/* eslint-enable @typescript-eslint/naming-convention */

/** Per-part retry budget for multipart upload (transient network failures). */
const PART_MAX_ATTEMPTS = 4;

export class FilesApiClient extends AuthenticatedApiClient {
  private get basePath(): string {
    return '/api/v1/files';
  }

  async requestUploadUrl(
    filename: string,
    contentType: string,
    fileSize: number
  ): Promise<UploadUrlResult> {
    const raw = await this.post<RawUploadUrlResult>(
      `${this.basePath}/upload-url`,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      {filename, content_type: contentType, file_size: fileSize}
    );
    return {url: raw.url, fileId: raw.file_id, expiresIn: raw.expires_in};
  }

  async uploadToPresignedUrl(
    url: string,
    file: globalThis.File,
    onProgress?: (fraction: number) => void,
    contentType?: string
  ): Promise<void> {
    await xhrPut(url, file, {
      contentType: contentType ?? file.type,
      onProgress: onProgress
        ? (loaded) => {
            onProgress(file.size > 0 ? Math.min(loaded / file.size, 1) : 1);
          }
        : undefined,
    });
  }

  // ── Multipart upload (large files) ────────────────────────────────────────

  async initMultipartUpload(
    filename: string,
    contentType: string,
    fileSize: number
  ): Promise<MultipartInitResult> {
    const raw = await this.post<RawMultipartInitResult>(
      `${this.basePath}/multipart/init`,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      {filename, content_type: contentType, file_size: fileSize}
    );
    return {
      fileId: raw.file_id,
      uploadId: raw.upload_id,
      partSize: raw.part_size,
      partCount: raw.part_count,
    };
  }

  async getMultipartPartUrls(
    fileId: string,
    uploadId: string,
    partNumbers: number[]
  ): Promise<Record<number, string>> {
    const raw = await this.post<RawMultipartPartUrls>(
      `${this.basePath}/multipart/${fileId}/part-urls`,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      {upload_id: uploadId, part_numbers: partNumbers}
    );
    const result: Record<number, string> = {};
    for (const [key, value] of Object.entries(raw.urls)) {
      result[Number(key)] = value;
    }

    return result;
  }

  async completeMultipartUpload(
    fileId: string,
    input: CompleteMultipartInput
  ): Promise<ManagedFile> {
    /* eslint-disable @typescript-eslint/naming-convention */
    const payload: Record<string, unknown> = {
      upload_id: input.uploadId,
      name: input.name,
      original_filename: input.originalFilename,
      content_type: input.contentType,
      parts: input.parts.map((p) => ({
        part_number: p.partNumber,
        etag: p.etag,
      })),
      association: {
        object_type_id: input.association.objectTypeId,
        object_record_id: input.association.objectRecordId,
      },
    };
    if (input.categoryId !== undefined) payload.category_id = input.categoryId;
    if (input.cui !== undefined) payload.cui = input.cui;
    /* eslint-enable @typescript-eslint/naming-convention */
    const raw = await this.post<RawManagedFile>(
      `${this.basePath}/multipart/${fileId}/complete`,
      payload
    );
    return this.toManagedFile(raw);
  }

  async abortMultipartUpload(fileId: string, uploadId: string): Promise<void> {
    await this.post(`${this.basePath}/multipart/${fileId}/abort`, {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      upload_id: uploadId,
    });
  }

  /**
   * Upload a large file via S3 multipart: init → upload each part to its
   * presigned URL (with per-part retry) → complete. Reports aggregate progress
   * (0–1) and aborts the upload on unrecoverable failure so no orphaned parts
   * linger. Returns the created File.
   */
  async uploadLargeFile(
    file: globalThis.File,
    input: Omit<CompleteMultipartInput, 'uploadId' | 'parts'>,
    onProgress?: (fraction: number) => void
  ): Promise<ManagedFile> {
    const {fileId, uploadId, partSize, partCount} =
      await this.initMultipartUpload(file.name, input.contentType, file.size);

    try {
      const partNumbers = Array.from({length: partCount}, (_, i) => i + 1);
      const urls = await this.getMultipartPartUrls(
        fileId,
        uploadId,
        partNumbers
      );

      // Per-part progress so the bar reflects total bytes sent across parts.
      const sentPerPart = Array.from<number>({length: partCount}).fill(0);
      const reportProgress = () => {
        if (!onProgress) return;
        const sent = sentPerPart.reduce((a, b) => a + b, 0);
        onProgress(Math.min(sent / file.size, 1));
      };

      const parts: Array<{partNumber: number; etag: string}> = [];
      // Parts are uploaded sequentially (bounded memory + connections); a failed
      // part is retried in place rather than restarting the whole upload.
      for (const partNumber of partNumbers) {
        const start = (partNumber - 1) * partSize;
        const blob = file.slice(start, Math.min(start + partSize, file.size));
        // eslint-disable-next-line no-await-in-loop
        const etag = await this.uploadPartWithRetry(
          urls[partNumber],
          blob,
          (loaded) => {
            sentPerPart[partNumber - 1] = loaded;
            reportProgress();
          }
        );
        sentPerPart[partNumber - 1] = blob.size;
        reportProgress();
        parts.push({partNumber, etag});
      }

      return await this.completeMultipartUpload(fileId, {
        ...input,
        uploadId,
        parts,
      });
    } catch (error) {
      // Best-effort cleanup so a failed upload doesn't leave dangling parts.
      try {
        await this.abortMultipartUpload(fileId, uploadId);
      } catch {
        // Ignore — the bucket lifecycle rule auto-aborts stale uploads.
      }

      throw error;
    }
  }

  async createFileFromStaging(
    input: CreateFileFromStagingInput
  ): Promise<ManagedFile> {
    /* eslint-disable @typescript-eslint/naming-convention */
    const payload: Record<string, unknown> = {
      file_id: input.fileId,
      name: input.name,
      original_filename: input.originalFilename,
      content_type: input.contentType,
    };
    if (input.categoryId !== undefined) payload.category_id = input.categoryId;
    if (input.associations !== undefined) {
      payload.associations = input.associations.map((a) => ({
        object_type_id: a.objectTypeId,
        object_record_id: a.objectRecordId,
      }));
    }

    if (input.cui !== undefined) payload.cui = input.cui;

    /* eslint-enable @typescript-eslint/naming-convention */
    const raw = await this.post<RawManagedFile>(this.basePath, payload);
    return this.toManagedFile(raw);
  }

  async createFileFromRelay(
    input: CreateFileFromRelayInput
  ): Promise<ManagedFile> {
    /* eslint-disable @typescript-eslint/naming-convention */
    const payload: Record<string, unknown> = {
      source_email_attachment_id: input.sourceEmailAttachmentId,
      name: input.name,
    };
    if (input.categoryId !== undefined) payload.category_id = input.categoryId;
    if (input.associations !== undefined) {
      payload.associations = input.associations.map((a) => ({
        object_type_id: a.objectTypeId,
        object_record_id: a.objectRecordId,
      }));
    }

    if (input.cui !== undefined) payload.cui = input.cui;

    /* eslint-enable @typescript-eslint/naming-convention */
    const raw = await this.post<RawManagedFile>(this.basePath, payload);
    return this.toManagedFile(raw);
  }

  async listFiles(params?: ListFilesParams): Promise<FilesListResponse> {
    const qp = new URLSearchParams();
    if (params?.associationRef)
      qp.set('association_ref', params.associationRef);
    if (params?.categoryId) qp.set('category_id', params.categoryId);
    if (params?.search) qp.set('search', params.search);
    if (params?.includeDeleted) qp.set('include_deleted', 'true');
    if (params?.missingRequiredAssociation) {
      qp.set('missing_required_association', 'true');
      for (const id of params.requiredObjectTypeIds ?? [])
        qp.append('required_object_type_ids', id);
    }

    if (params?.limit !== undefined) qp.set('limit', String(params.limit));
    if (params?.offset !== undefined) qp.set('offset', String(params.offset));

    const qs = qp.toString();
    const url = qs ? `${this.basePath}?${qs}` : this.basePath;
    const raw = await this.get<RawFilesListResponse>(url);
    return {
      items: raw.items.map((f) => this.toManagedFile(f)),
      total: raw.total,
    };
  }

  async getFile(id: string): Promise<ManagedFile> {
    const raw = await this.get<RawManagedFile>(`${this.basePath}/${id}`);
    return this.toManagedFile(raw);
  }

  async updateFile(id: string, input: UpdateFileInput): Promise<ManagedFile> {
    const payload: Record<string, unknown> = {};
    if (input.name !== undefined) payload.name = input.name;

    if ('categoryId' in input) payload.category_id = input.categoryId ?? null;

    const raw = await this.patch<RawManagedFile>(
      `${this.basePath}/${id}`,
      payload
    );
    return this.toManagedFile(raw);
  }

  /**
   * @param disposition - 'inline' (default, backend behavior unchanged) shows
   * the file in-browser; 'attachment' sets Content-Disposition: attachment on
   * the presigned S3 URL so opening it triggers a real download instead of
   * rendering inline (the fix for "download" buttons that just opened the file).
   */
  async getContentUrl(
    id: string,
    disposition?: 'inline' | 'attachment'
  ): Promise<ContentUrlResult> {
    const qs = disposition ? `?disposition=${disposition}` : '';
    const raw = await this.get<RawContentUrlResult>(
      `${this.basePath}/${id}/content-url${qs}`
    );
    return {url: raw.url, expiresIn: raw.expires_in};
  }

  async softDeleteFile(id: string): Promise<void> {
    await this.delete(`${this.basePath}/${id}`);
  }

  async hardDeleteFile(id: string): Promise<void> {
    await this.delete(`${this.basePath}/${id}/hard-delete`);
  }

  async addAssociation(
    fileId: string,
    objectTypeId: string,
    objectRecordId: string | null
  ): Promise<FileAssociation> {
    const raw = await this.post<RawFileAssociation>(
      `${this.basePath}/${fileId}/associations`,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      {object_type_id: objectTypeId, object_record_id: objectRecordId}
    );
    return this.toAssociation(raw);
  }

  async removeAssociation(fileId: string, assocId: string): Promise<void> {
    await this.delete(`${this.basePath}/${fileId}/associations/${assocId}`);
  }

  async listCategories(): Promise<FileCategory[]> {
    const raw = await this.get<{items: RawFileCategory[]}>(
      `${this.basePath}/categories`
    );
    return raw.items.map((c) => this.toCategory(c));
  }

  async createCategory(
    name: string,
    sortOrder?: number
  ): Promise<FileCategory> {
    const payload: Record<string, unknown> = {name};

    if (sortOrder !== undefined) payload.sort_order = sortOrder;
    const raw = await this.post<RawFileCategory>(
      `${this.basePath}/categories`,
      payload
    );
    return this.toCategory(raw);
  }

  async updateCategory(
    id: string,
    input: {name?: string; sortOrder?: number}
  ): Promise<FileCategory> {
    const payload: Record<string, unknown> = {};
    if (input.name !== undefined) payload.name = input.name;

    if (input.sortOrder !== undefined) payload.sort_order = input.sortOrder;
    const raw = await this.patch<RawFileCategory>(
      `${this.basePath}/categories/${id}`,
      payload
    );
    return this.toCategory(raw);
  }

  async deleteCategory(id: string): Promise<void> {
    await this.delete(`${this.basePath}/categories/${id}`);
  }

  private async uploadPartWithRetry(
    url: string,
    blob: Blob,
    onProgress: (loaded: number) => void
  ): Promise<string> {
    let lastError: unknown;
    for (let attempt = 1; attempt <= PART_MAX_ATTEMPTS; attempt++) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const {etag} = await xhrPut(url, blob, {onProgress});
        if (!etag) throw new Error('Missing ETag on uploaded part');
        return etag;
      } catch (error) {
        lastError = error;
        onProgress(0); // Reset this part's progress before retrying
        if (attempt < PART_MAX_ATTEMPTS) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => {
            setTimeout(resolve, 2 ** attempt * 500);
          });
        }
      }
    }

    throw lastError instanceof Error
      ? lastError
      : new Error('Part upload failed');
  }

  private toAssociation(raw: RawFileAssociation): FileAssociation {
    return {
      id: raw.id,
      objectTypeId: raw.object_type_id,
      objectRecordId: raw.object_record_id,
      objectTypeName: raw.object_type_name,
      objectRecordName: raw.object_record_name,
    };
  }

  private toManagedFile(raw: RawManagedFile): ManagedFile {
    return {
      id: raw.id,
      name: raw.name,
      originalFilename: raw.original_filename,
      contentType: raw.content_type,
      sizeBytes: raw.size_bytes,
      sha256: raw.sha256,
      categoryId: raw.category_id,
      labels: raw.labels ?? (raw.category_id ? [raw.category_id] : []),
      sourceKind: raw.source_kind,
      sourceId: raw.source_id,
      cui: Boolean(raw.cui),
      createdBy: raw.created_by,
      createdAt: raw.created_at,
      updatedAt: raw.updated_at,
      deletedAt: raw.deleted_at,
      emailSentAt: raw.email_sent_at,
      associations: raw.associations.map((a) => this.toAssociation(a)),
    };
  }

  private toCategory(raw: RawFileCategory): FileCategory {
    return {
      id: raw.id,
      name: raw.name,
      sortOrder: raw.sort_order,
      deletedAt: raw.deleted_at,
    };
  }
}

const clientCache = new Map<string, FilesApiClient>();

export function getFilesApi(token?: string): FilesApiClient {
  const key = token ?? 'no-token';
  if (!clientCache.has(key)) {
    clientCache.set(key, new FilesApiClient(token));
  }

  return clientCache.get(key)!;
}
